import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/dist/query/react';
import { getSettings, SettingsState } from '@vxui/common';
import { Network } from '@vxui/common/src/models/config';
import { Config, ConfigResponse } from '../models';

const rawBaseQuery = fetchBaseQuery();
const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const settings = getSettings(api.getState() as SettingsState);
  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = `${settings?.servers.serverOTTGateway ?? ''}/api/v2/${urlEnd}`;
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };
  return rawBaseQuery(adjustedArgs, api, extraOptions);
};

const configTag = 'Config' as const;

export const configApi = createApi({
  reducerPath: 'configApi',
  tagTypes: [configTag],
  baseQuery: dynamicBaseQuery,
  endpoints: builder => ({
    getConfig: builder.query<Config, void>({
      query: () => 'config',
      transformResponse: (response: ConfigResponse) => {
        const results = response?.data ?? {};
        results.network?.forEach(value => {
          if (!value.addresses) value.addresses = [];
          if (!value.routes) value.routes = [];
          if (!value.dns) value.dns = [];
          if (!value.gateway) value.gateway = '';
        });
        return results;
      },
      providesTags: [configTag],
    }),
    updateNetworkSettings: builder.mutation<void, Network>({
      query: network => ({
        url: `config/${network.id}`,
        method: 'PUT',
        body: { data: network },
      }),
    }),
    setPendingReboot: builder.mutation<void, Network>({
      query: () => ({
        url: `config/setpendingreboot`,
        method: 'POST',
      }),
      invalidatesTags: [configTag],
    }),
    setForceReboot: builder.mutation<void, void>({
      query: () => ({
        url: `config/forcereboot`,
        method: 'POST',
      }),
      invalidatesTags: [configTag],
    }),
  }),
});

export const {
  useGetConfigQuery,
  useUpdateNetworkSettingsMutation,
  useSetPendingRebootMutation,
  useSetForceRebootMutation,
} = configApi;
